import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './AdminServicios.css';

const AdminServicios = () => {
    const [servicios, setServicios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState('grid');
    const [filterStatus, setFilterStatus] = useState('all');

    const [showModal, setShowModal] = useState(false);
    const [editingServicio, setEditingServicio] = useState(null);
    const [formData, setFormData] = useState({
        nombre: '',
        descripcion: '',
        precio: '',
        precioSena: '',
        duracion: 60,
        activo: true,
    });
    const [formErrors, setFormErrors] = useState({});

    // ============================================
    // CARGAR SERVICIOS
    // ============================================
    const fetchServicios = async () => {
        try {
            setLoading(true);
            const res = await api.get('/admin/servicios');
            setServicios(res.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Error al cargar servicios');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchServicios(); }, []);

    // ============================================
    // FILTROS
    // ============================================
    const filtered = servicios.filter(s => {
        const term = searchTerm.toLowerCase();
        const matchesSearch = !searchTerm ||
            s.nombre.toLowerCase().includes(term) ||
            (s.descripcion || '').toLowerCase().includes(term);
        const matchesStatus = filterStatus === 'all' ||
            (filterStatus === 'activo' && s.activo) ||
            (filterStatus === 'inactivo' && !s.activo);
        return matchesSearch && matchesStatus;
    });

    // ============================================
    // FORM
    // ============================================
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.nombre.trim()) errors.nombre = 'El nombre es obligatorio';
        if (formData.precio === '' || Number(formData.precio) < 0) errors.precio = 'Ingresá un precio válido';
        if (formData.precioSena === '' || Number(formData.precioSena) < 0) errors.precioSena = 'Ingresá una seña válida';
        if (!formData.duracion || Number(formData.duracion) <= 0) errors.duracion = 'Ingresá una duración válida';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const resetForm = () => {
        setFormData({
            nombre: '',
            descripcion: '',
            precio: '',
            precioSena: '',
            duracion: 60,
            activo: true,
        });
        setFormErrors({});
        setEditingServicio(null);
    };

    const openNew = () => {
        resetForm();
        setShowModal(true);
    };

    const openEdit = (servicio) => {
        setEditingServicio(servicio);
        setFormData({
            nombre: servicio.nombre || '',
            descripcion: servicio.descripcion || '',
            precio: servicio.precio ?? '',
            precioSena: servicio.precioSena ?? '',
            duracion: servicio.duracion ?? 60,
            activo: servicio.activo ?? true,
        });
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            const payload = {
                ...formData,
                precio: Number(formData.precio),
                precioSena: Number(formData.precioSena),
                duracion: Number(formData.duracion),
            };

            if (editingServicio) {
                await api.put(`/admin/servicios/${editingServicio._id}`, payload);
            } else {
                await api.post('/admin/servicios', payload);
            }

            setShowModal(false);
            resetForm();
            fetchServicios();
        } catch (err) {
            alert(err.response?.data?.message || 'Error al guardar');
        }
    };

    const toggleStatus = async (id) => {
        try {
            await api.patch(`/admin/servicios/${id}/status`);
            fetchServicios();
        } catch (err) {
            alert('Error al cambiar estado');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Eliminar este servicio permanentemente?')) return;
        try {
            await api.delete(`/admin/servicios/${id}`);
            fetchServicios();
        } catch (err) {
            alert('Error al eliminar');
        }
    };

    // ============================================
    // HELPERS
    // ============================================
    const formatearPrecio = (valor) => {
        return new Intl.NumberFormat('es-AR', {
            style: 'currency',
            currency: 'ARS',
            minimumFractionDigits: 0,
        }).format(valor || 0);
    };

    const formatearDuracion = (min) => {
        if (min < 60) return `${min} min`;
        const h = Math.floor(min / 60);
        const m = min % 60;
        return m === 0 ? `${h} h` : `${h} h ${m} min`;
    };

    // ============================================
    // VISTAS
    // ============================================
    const renderGrid = () => (
        <div className="servicios-grid">
            {filtered.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-state-icon">💆</div>
                    <div className="empty-state-title">No hay servicios</div>
                    <div className="empty-state-text">Creá tu primer tipo de masaje</div>
                </div>
            ) : (
                filtered.map(s => (
                    <div key={s._id} className="servicio-card">
                        <div className="servicio-card-header">
                            <div className="servicio-nombre">{s.nombre}</div>
                            <span className={`estado-badge ${s.activo ? 'active' : 'inactive'}`}>
                                <span className={`estado-dot ${s.activo ? 'active' : 'inactive'}`}></span>
                                {s.activo ? 'Activo' : 'Inactivo'}
                            </span>
                        </div>
                        <div className="servicio-card-body">
                            {s.descripcion && (
                                <p className="servicio-descripcion">{s.descripcion}</p>
                            )}
                            <div className="servicio-precios">
                                <div className="servicio-precio-row">
                                    <span className="precio-label">💰 Precio total</span>
                                    <span className="precio-valor">{formatearPrecio(s.precio)}</span>
                                </div>
                                <div className="servicio-precio-row sena">
                                    <span className="precio-label">📌 Seña para reservar</span>
                                    <span className="precio-valor">{formatearPrecio(s.precioSena)}</span>
                                </div>
                            </div>
                            <div className="servicio-meta">
                                <span>⏱️ {formatearDuracion(s.duracion)}</span>
                            </div>
                        </div>
                        <div className="servicio-card-footer">
                            <button className="btn-client-action btn-edit" onClick={() => openEdit(s)}>
                                ✏️ Editar
                            </button>
                            <button
                                className={`btn-client-action ${s.activo ? 'btn-inactive' : 'btn-active'}`}
                                onClick={() => toggleStatus(s._id)}
                            >
                                {s.activo ? '🔒 Desactivar' : '✅ Activar'}
                            </button>
                            <button className="btn-client-action btn-delete" onClick={() => handleDelete(s._id)}>
                                🗑️
                            </button>
                        </div>
                    </div>
                ))
            )}
        </div>
    );

    const renderList = () => (
        <div className="servicios-list">
            {filtered.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-state-icon">💆</div>
                    <div className="empty-state-title">No hay servicios</div>
                </div>
            ) : (
                filtered.map(s => (
                    <div key={s._id} className="servicio-list-item">
                        <div className="servicio-list-info">
                            <div className="servicio-list-name">{s.nombre}</div>
                            <div className="servicio-list-contact">
                                <span>💰 {formatearPrecio(s.precio)}</span>
                                <span>📌 Seña: {formatearPrecio(s.precioSena)}</span>
                                <span>⏱️ {formatearDuracion(s.duracion)}</span>
                            </div>
                        </div>
                        <div className="servicio-list-status">
                            <span className={`estado-badge ${s.activo ? 'active' : 'inactive'}`}>
                                <span className={`estado-dot ${s.activo ? 'active' : 'inactive'}`}></span>
                                {s.activo ? 'Activo' : 'Inactivo'}
                            </span>
                        </div>
                        <div className="servicio-list-actions">
                            <button className="btn-client-action btn-edit" onClick={() => openEdit(s)}>✏️</button>
                            <button
                                className={`btn-client-action ${s.activo ? 'btn-inactive' : 'btn-active'}`}
                                onClick={() => toggleStatus(s._id)}
                            >
                                {s.activo ? '🔒' : '✅'}
                            </button>
                            <button className="btn-client-action btn-delete" onClick={() => handleDelete(s._id)}>🗑️</button>
                        </div>
                    </div>
                ))
            )}
        </div>
    );

    // ============================================
    // RENDER
    // ============================================
    if (loading) {
        return (
            <div className="admin-servicios">
                <div className="admin-page-loading">Cargando servicios...</div>
            </div>
        );
    }

    return (
        <div className="admin-servicios">
           {/* <div className="admin-servicios-header">
                <h1>💆 Servicios de Masaje</h1>
                <p className="admin-servicios-subtitle">
                    Gestioná los tipos de masaje, precios y señas de reserva
                </p>
            </div>*/}

            {error && <div className="alert alert-danger">{error}</div>}

            {/* ESTADÍSTICAS COMPACTAS */}
            <div className="stats-compact">
                <div className="stat-card-compact primary">
                    <div className="stat-icon">💆</div>
                    <div className="stat-content">
                        <div className="stat-value">{servicios.length}</div>
                        <div className="stat-label">Total servicios</div>
                    </div>
                </div>
                <div className="stat-card-compact success">
                    <div className="stat-icon">✅</div>
                    <div className="stat-content">
                        <div className="stat-value">{servicios.filter(s => s.activo).length}</div>
                        <div className="stat-label">Activos</div>
                    </div>
                </div>
                <div className="stat-card-compact danger">
                    <div className="stat-icon">🚫</div>
                    <div className="stat-content">
                        <div className="stat-value">{servicios.filter(s => !s.activo).length}</div>
                        <div className="stat-label">Inactivos</div>
                    </div>
                </div>
                <div className="stat-card-compact info" style={{ cursor: 'pointer' }} onClick={openNew}>
                    <div className="stat-icon">➕</div>
                    <div className="stat-content">
                        <div className="stat-value" style={{ fontSize: '1.1rem', marginTop: '0.2rem' }}>Nuevo</div>
                    </div>
                </div>
            </div>

            {/* Barra de herramientas */}
            <div className="servicios-section">
                <div className="section-header">
                    <div className="section-title">
                        💆 Lista de Servicios
                        <span style={{ fontSize: '0.8rem', color: '#8A7A6A', fontWeight: 'normal' }}>
                            ({filtered.length} mostrados)
                        </span>
                    </div>
                    <div className="section-actions">
                        <div className="search-box">
                            <span className="search-icon">🔍</span>
                            <input
                                type="text"
                                className="search-input"
                                placeholder="Buscar por nombre o descripción..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="filter-group">
                            <button
                                className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
                                onClick={() => setFilterStatus('all')}
                            >
                                Todos
                            </button>
                            <button
                                className={`filter-btn ${filterStatus === 'activo' ? 'active' : ''}`}
                                onClick={() => setFilterStatus('activo')}
                            >
                                ✅ Activos
                            </button>
                            <button
                                className={`filter-btn ${filterStatus === 'inactivo' ? 'active' : ''}`}
                                onClick={() => setFilterStatus('inactivo')}
                            >
                                🚫 Inactivos
                            </button>
                        </div>
                        <div className="toggle-group">
                            <button
                                className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                                onClick={() => setViewMode('grid')}
                            >
                                ▦ Grid
                            </button>
                            <button
                                className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                                onClick={() => setViewMode('list')}
                            >
                                ≡ Lista
                            </button>
                        </div>
                    </div>
                </div>

                {viewMode === 'grid' ? renderGrid() : renderList()}
            </div>

            {/* Modal */}
            {showModal && (
                <div className="modal-backdrop" onClick={() => { setShowModal(false); resetForm(); }}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h5 className="modal-title">
                                {editingServicio ? 'Editar Servicio' : 'Nuevo Servicio'}
                            </h5>
                            <button className="btn-close" onClick={() => { setShowModal(false); resetForm(); }}>×</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label>Nombre del servicio *</label>
                                    <input
                                        type="text"
                                        name="nombre"
                                        className={`form-control ${formErrors.nombre ? 'is-invalid' : ''}`}
                                        value={formData.nombre}
                                        onChange={handleInputChange}
                                        placeholder="Ej: Masaje Tailandés"
                                        required
                                    />
                                    {formErrors.nombre && <small className="error-text">{formErrors.nombre}</small>}
                                </div>

                                <div className="form-group">
                                    <label>Descripción</label>
                                    <textarea
                                        name="descripcion"
                                        className="form-control"
                                        value={formData.descripcion}
                                        onChange={handleInputChange}
                                        rows="2"
                                        placeholder="Detalles del masaje (opcional)"
                                    />
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Precio total *</label>
                                        <input
                                            type="number"
                                            name="precio"
                                            className={`form-control ${formErrors.precio ? 'is-invalid' : ''}`}
                                            value={formData.precio}
                                            onChange={handleInputChange}
                                            min="0"
                                            step="100"
                                            placeholder="60000"
                                            required
                                        />
                                        {formErrors.precio && <small className="error-text">{formErrors.precio}</small>}
                                    </div>

                                    <div className="form-group">
                                        <label>Seña para reservar *</label>
                                        <input
                                            type="number"
                                            name="precioSena"
                                            className={`form-control ${formErrors.precioSena ? 'is-invalid' : ''}`}
                                            value={formData.precioSena}
                                            onChange={handleInputChange}
                                            min="0"
                                            step="100"
                                            placeholder="20000"
                                            required
                                        />
                                        {formErrors.precioSena && <small className="error-text">{formErrors.precioSena}</small>}
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Duración (minutos) *</label>
                                    <select
                                        name="duracion"
                                        className="form-control"
                                        value={formData.duracion}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value={30}>30 minutos</option>
                                        <option value={45}>45 minutos</option>
                                        <option value={60}>1 hora</option>
                                        <option value={75}>1 hora 15 min</option>
                                        <option value={90}>1 hora 30 min</option>
                                        <option value={120}>2 horas</option>
                                    </select>
                                </div>

                                <div className="form-group form-check">
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            name="activo"
                                            checked={formData.activo}
                                            onChange={handleInputChange}
                                        />
                                        <span>Servicio activo (visible para reservar)</span>
                                    </label>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); resetForm(); }}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    {editingServicio ? 'Guardar cambios' : 'Crear servicio'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminServicios;